import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { createPaymentIntent, finalizePayment, estimateShippingCost } from '../firebase';
import { getCountryFlag } from '../utils/countryUtils';
import { DEFAULT_QUIKBAK_FEE } from '../utils/shippingUtils';
import ProhibitedItemsAcknowledgment from './ProhibitedItemsAcknowledgment';

// Japan is the only supported shipping destination
const JAPAN_COUNTRY_CODE = 'JP';
const JAPAN_COUNTRY_NAME = 'Japan';
const JAPAN_UNSHIPPABLE_POSTAL_CODE_RESULT_CODE = 'E2-9001';

// API endpoint for checkAddress (uses emulator in dev, production in prod)
const getCheckAddressUrl = (postalCode, hotelId) => {
  const useEmulator = process.env.REACT_APP_USE_FUNCTIONS_EMULATOR !== 'false' &&
                      process.env.NODE_ENV === 'development';
  const baseUrl = useEmulator
  ? 'http://localhost:5004/missingmatcher/us-central1/sagawaApi'
    : 'https://us-central1-missingmatcher.cloudfunctions.net/sagawaApi';
  const query = hotelId ? `?hotelId=${encodeURIComponent(hotelId)}` : '';
  return `${baseUrl}/checkaddress/${encodeURIComponent(postalCode)}${query}`;
};

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

const zeroDecimalCurrencies = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'vnd', 'xaf', 'xof', 'xpf'
]);

const normalizeJapanPostalCode = (value) => (typeof value === 'string' ? value.replace(/\D/g, '') : '');

const formatAmount = (amount, currency = 'jpy') => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return '';
  }
  const normalizedCurrency = currency.toLowerCase();
  const isZeroDecimal = zeroDecimalCurrencies.has(normalizedCurrency);
  const divisor = isZeroDecimal ? 1 : 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'JPY').toUpperCase(),
    minimumFractionDigits: isZeroDecimal ? 0 : 2,
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
  }).format(amount / divisor);
};

const resolveCallableErrorMessage = (error, translate) => {
  if (!error) {
    return translate('payment.messages.error');
  }
  switch (error.code) {
    case 'functions/failed-precondition':
      return error.message || translate('payment.errors.missingAmount');
    case 'functions/not-found':
      return translate('payment.errors.notFound');
    default:
      return error.message || translate('payment.messages.error');
  }
};

const normalizeStoredAddress = (address) => {
  if (!address) return null;
  // Always use Japan for shipping
  return {
    name: address.name || '',
    line1: address.line1 || '',
    line2: address.line2 || '',
    city: address.city || '',
    state: address.state || '',
    postalCode: address.postalCode || '',
    countryCode: JAPAN_COUNTRY_CODE,
    countryName: JAPAN_COUNTRY_NAME,
    phone: address.phone || '',
  };
};

const buildShippingPayload = (address) => ({
  name: address.name,
  line1: address.line1,
  line2: address.line2,
  city: address.city,
  state: address.state,
  postalCode: address.postalCode,
  country: address.countryCode || address.countryName,
  countryCode: address.countryCode,
  countryName: address.countryName,
  phone: address.phone,
});

const buildStripeShipping = (address) => ({
  name: address.name,
  phone: address.phone || undefined,
  address: {
    line1: address.line1,
    line2: address.line2 || undefined,
    city: address.city,
    state: address.state || undefined,
    postal_code: address.postalCode,
    country: (address.countryCode || '').toUpperCase(),
  },
});

function CheckoutForm({
  matchId,
  paymentIntentId,
  amount,
  currency,
  shippingDetails,
  onPaymentComplete,
  onError,
  onSuccess,
  onProcessingChange,
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) {
      return;
    }

    setSubmitting(true);
    setLocalError('');
    onError('');
    if (typeof onProcessingChange === 'function') {
      onProcessingChange(true);
    }

    const confirmOptions = {
      elements,
      redirect: 'if_required',
    };

    const result = await stripe.confirmPayment(confirmOptions);

    if (result.error) {
      const message = result.error.message || t('payment.messages.error');
      setLocalError(message);
      onError(message);
      if (typeof onProcessingChange === 'function') {
        onProcessingChange(false);
      }
      setSubmitting(false);
      return;
    }

    const resolvedIntent = result.paymentIntent;
    if (!resolvedIntent) {
      const message = t('payment.messages.missingIntent');
      setLocalError(message);
      onError(message);
      if (typeof onProcessingChange === 'function') {
        onProcessingChange(false);
      }
      setSubmitting(false);
      return;
    }

    try {
      const finalizeResult = await finalizePayment({
        matchId,
        paymentIntentId: resolvedIntent.id || paymentIntentId,
      });

      const payload = finalizeResult?.data || finalizeResult;
      onPaymentComplete(payload);
      onSuccess(t('payment.messages.success'));
    } catch (error) {
      const message = resolveCallableErrorMessage(error, t);
      setLocalError(message);
      onError(message);
    } finally {
      setSubmitting(false);
      if (typeof onProcessingChange === 'function') {
        onProcessingChange(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <PaymentElement options={{ layout: 'tabs' }} />
      {localError && <div className="error-message" role="alert">{localError}</div>}
      <div className="payment-action-row">
        <div className="payment-amount">
          <strong>{t('payment.amount')}:</strong> {formatAmount(amount, currency)}
        </div>
        <button type="submit" className="payment-submit-button" disabled={!stripe || submitting}>
          {submitting ? t('payment.buttons.processing') : t('payment.buttons.payNow')}
        </button>
      </div>
    </form>
  );
}

const ShippingBreakdown = ({ t, shippingDetails, shippingCost, quikbakFee, total, currency, packageWeight, showAddress = true, title }) => {
  const countryDisplay = shippingDetails?.countryName || shippingDetails?.countryCode || '';
  const flag = shippingDetails?.countryCode ? getCountryFlag(shippingDetails.countryCode) : getCountryFlag(countryDisplay);
  const hasCountry = Boolean(countryDisplay);

  return (
    <div className="payment-breakdown-card">
      <div className="payment-breakdown-header">{title || t('payment.breakdown.title')}</div>
      <div className="payment-breakdown-body">
        <div className="payment-breakdown-row">
          <span>
            {t('payment.breakdown.shippingCost')}
            {typeof packageWeight === 'number' && packageWeight > 0 && (
              <span style={{ marginLeft: '8px', color: '#7f8c8d' }}>
                ({packageWeight} kg)
              </span>
            )}
            {hasCountry && (
              <span className="shipping-country-pill">
                <span className="flag">{flag}</span>
                <span>{countryDisplay}</span>
              </span>
            )}
          </span>
          <span className="payment-breakdown-amount">
            {typeof shippingCost === 'number' && shippingCost > 0
              ? formatAmount(shippingCost, currency)
              : t('payment.messages.amountPending')}
          </span>
        </div>
        <div className="payment-breakdown-row">
          <span>{t('payment.breakdown.quikbakFee')}</span>
          <span className="payment-breakdown-amount">{formatAmount(quikbakFee, currency)}</span>
        </div>
      </div>
      <div className="payment-breakdown-total">
        <span>{t('payment.breakdown.total')}</span>
        <span>{typeof total === 'number' ? formatAmount(total, currency) : t('payment.messages.amountPending')}</span>
      </div>
      {showAddress && shippingDetails && (
        <div className="shipping-address-preview">
          <div className="shipping-address-label">{t('payment.breakdown.shippingAddress')}</div>
          <address>
            <div>{shippingDetails.name}</div>
            <div>{shippingDetails.line1}</div>
            {shippingDetails.line2 && shippingDetails.line2.trim() !== '' && (
              <div>{shippingDetails.line2}</div>
            )}
            <div>
              {[shippingDetails.city, shippingDetails.state]
                .filter(Boolean)
                .join(', ')}{shippingDetails.postalCode ? ` ${shippingDetails.postalCode}` : ''}
            </div>
            <div>
              <span style={{ marginRight: '6px' }}>{flag}</span>
              {countryDisplay}
            </div>
            {shippingDetails.phone && shippingDetails.phone.trim() !== '' && (
              <div>{shippingDetails.phone}</div>
            )}
          </address>
        </div>
      )}
    </div>
  );
};

// Japan address form with postal code lookup - two-phase: name/postal first, then full address
const JapanAddressStep = ({ initialAddress, onSubmit, onPostalCodeChange = () => {}, formError, disabled = false, hotelId }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [lookingUpAddress, setLookingUpAddress] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState('');

  // Form state
  const [name, setName] = useState(initialAddress?.name || '');
  const [postalCode, setPostalCode] = useState(initialAddress?.postalCode || '');
  const [state, setState] = useState(initialAddress?.state || ''); // Prefecture
  const [city, setCity] = useState(initialAddress?.city || ''); // City + District
  const [line1, setLine1] = useState(initialAddress?.line1 || ''); // Street address
  const [line2, setLine2] = useState(initialAddress?.line2 || ''); // Building/Apartment
  const [phone, setPhone] = useState(initialAddress?.phone || '');

  // Track if address has been looked up successfully
  const [addressResolved, setAddressResolved] = useState(
    Boolean(initialAddress?.state && initialAddress?.city)
  );

  // Auto-lookup address when postal code is 7 digits
  useEffect(() => {
    const cleanPostal = postalCode.replace(/[-\s]/g, '');
    if (cleanPostal.length !== 7 || !/^\d{7}$/.test(cleanPostal)) {
      return;
    }

    let cancelled = false;

    const lookupAddress = async () => {
      setLookingUpAddress(true);
      setAddressLookupError('');

      try {
        const response = await fetch(getCheckAddressUrl(cleanPostal, initialAddress?.hotelId || hotelId));
        if (!response.ok) {
          throw new Error('Address lookup failed');
        }
        const data = await response.json();

        if (cancelled) return;

        if (data.resultCode === JAPAN_UNSHIPPABLE_POSTAL_CODE_RESULT_CODE) {
          setAddressLookupError(t('payment.errors.unshippablePostalCode') || 'Shipping is not available for this postal code because this destination requires air transport, which is not supported.');
          setAddressResolved(false);
        } else if (data.resultCode?.startsWith('S0') && data.addresses?.length > 0) {
          const addr = data.addresses[0];
          // Set prefecture (state)
          if (addr.todofukenName) {
            setState(addr.todofukenName);
          }
          // Set city + district
          const cityDistrict = [addr.shikuchosonName, addr.choikiName].filter(Boolean).join('');
          if (cityDistrict) {
            setCity(cityDistrict);
          }
          // Mark address as resolved to show remaining fields
          setAddressResolved(true);
        } else {
          setAddressLookupError(t('payment.errors.postalCodeNotFound') || 'Address not found for this postal code');
          setAddressResolved(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Address lookup error:', error);
          setAddressLookupError(t('payment.errors.addressLookupFailed') || 'Failed to lookup address');
          setAddressResolved(false);
        }
      } finally {
        if (!cancelled) {
          setLookingUpAddress(false);
        }
      }
    };

    lookupAddress();

    return () => {
      cancelled = true;
    };
  }, [postalCode, t]);

  useEffect(() => {
    onPostalCodeChange(normalizeJapanPostalCode(postalCode));
  }, [onPostalCodeChange, postalCode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (disabled || submitting) {
      return;
    }

    // Validate required fields
    if (!name.trim()) {
      onSubmit(null, t('payment.errors.missingField', { field: t('payment.fields.name') }));
      return;
    }
    if (!postalCode.trim()) {
      onSubmit(null, t('payment.errors.missingField', { field: t('payment.fields.postalCode') }));
      return;
    }
    if (!state.trim()) {
      onSubmit(null, t('payment.errors.missingField', { field: t('payment.fields.state') }));
      return;
    }
    if (!city.trim()) {
      onSubmit(null, t('payment.errors.missingField', { field: t('payment.fields.city') }));
      return;
    }
    if (!line1.trim()) {
      onSubmit(null, t('payment.errors.missingField', { field: t('payment.fields.address') }));
      return;
    }
    if (!phone.trim()) {
      onSubmit(null, t('payment.errors.missingField', { field: t('payment.fields.phone') }));
      return;
    }

    setSubmitting(true);

    const address = {
      name: name.trim(),
      postalCode: postalCode.replace(/[-\s]/g, ''),
      state: state.trim(),
      city: city.trim(),
      line1: line1.trim(),
      line2: line2.trim(),
      phone: phone.trim(),
      countryCode: JAPAN_COUNTRY_CODE,
      countryName: JAPAN_COUNTRY_NAME,
    };

    try {
      await onSubmit(address);
    } finally {
      setSubmitting(false);
    }
  };

  // Format postal code as user types (XXX-XXXX)
  const handlePostalCodeChange = (e) => {
    const inputType = e.nativeEvent?.inputType;
    const isBackwardDelete = inputType === 'deleteContentBackward';
    const digits = e.target.value.replace(/\D/g, '').slice(0, 7);

    let value = '';
    if (digits.length === 0) {
      value = '';
    } else if (digits.length < 3) {
      value = digits;
    } else if (digits.length === 3) {
      // Keep auto hyphen on normal typing, but allow backspace to remove it.
      value = isBackwardDelete ? digits : `${digits}-`;
    } else {
      value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }

    setPostalCode(value);
    // Reset address resolution when postal code changes
    if (digits.length < 7) {
      setAddressResolved(false);
      setState('');
      setCity('');
    }
  };

  return (
    <form className="shipping-form japan-address-form" onSubmit={handleSubmit}>
      <div className="japan-only-notice">
        <span className="flag">{getCountryFlag('JP')}</span>
        <span>{t('payment.japanOnly') || 'Shipping to Japan only'}</span>
      </div>

      {/* Phase 1: Name and Postal Code */}
      <div className="shipping-grid">
        <label className="shipping-field full-width">
          <span>{t('payment.fields.name')} *</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('payment.placeholders.name') || 'Full name'}
            disabled={disabled}
            required
          />
        </label>

        <label className="shipping-field postal-code-field full-width">
          <span>{t('payment.fields.postalCode')} *</span>
          <div className="postal-code-input-wrapper">
            <input
              type="text"
              value={postalCode}
              onChange={handlePostalCodeChange}
              placeholder={t('payment.placeholders.postalCode') || '123-4567'}
              disabled={disabled}
              required
              maxLength={8}
            />
            {lookingUpAddress && <span className="postal-lookup-spinner" />}
          </div>
          {addressLookupError && <span className="field-error">{addressLookupError}</span>}
        </label>
      </div>

      {/* Phase 2: Show remaining fields after postal code resolves */}
      {addressResolved && (
        <>
          <div className="address-preview">
            <span className="address-preview-label">{t('payment.addressFound') || 'Address found'}:</span>
            <span className="address-preview-value">{state} {city}</span>
          </div>

          <div className="shipping-grid">
            <label className="shipping-field full-width">
              <span>{t('payment.fields.address')} * ({t('payment.fields.streetAddress') || 'Street Address'})</span>
              <input
                type="text"
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                placeholder={t('payment.placeholders.address') || '1-2-3'}
                disabled={disabled}
                required
                autoFocus
              />
            </label>

            <label className="shipping-field full-width">
              <span>{t('payment.fields.address2')} ({t('payment.fields.building') || 'Building/Apartment'})</span>
              <input
                type="text"
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                placeholder={t('payment.placeholders.address2') || 'Building name, Room number'}
                disabled={disabled}
              />
            </label>

            <label className="shipping-field full-width">
              <span>{t('payment.fields.phone')} *</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('payment.placeholders.phone') || '090-1234-5678'}
                disabled={disabled}
                required
              />
            </label>
          </div>
        </>
      )}

      {formError && <div className="error-message" role="alert">{formError}</div>}

      <div className="payment-action-row">
        <button
          type="submit"
          className="payment-submit-button"
          disabled={submitting || disabled || lookingUpAddress || !addressResolved}
        >
          {submitting ? t('payment.buttons.processing') : t('payment.buttons.continue')}
        </button>
      </div>
    </form>
  );
};

export default function PaymentSection({ matchId, matchData, onPaymentComplete, onMatchUpdate = () => {} }) {
  const { t } = useTranslation();
  const initialShipping = useMemo(() => normalizeStoredAddress(matchData?.shippingAddress), [matchData?.shippingAddress]);
  const initialCurrency = (matchData?.paymentCurrency || 'jpy').toLowerCase();
  // Default to 'pending' if paymentStatus doesn't exist but match is in waiting-for-payment status
  const paymentStatus = matchData?.paymentStatus || (matchData?.status === 'waiting-for-payment' ? 'pending' : '');
  const hasSucceeded = paymentStatus === 'succeeded';

  // Japan-only shipping - start directly at address step
  const [step, setStep] = useState(() => (hasSucceeded ? 'completed' : 'address'));
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [showProhibitedItemsDialog, setShowProhibitedItemsDialog] = useState(false);
  const [hasAcknowledgedProhibited, setHasAcknowledgedProhibited] = useState(false);
  const [pendingAddress, setPendingAddress] = useState(null);
  const [pendingErrorOverride, setPendingErrorOverride] = useState('');

  const [statusMessage, setStatusMessage] = useState(hasSucceeded ? t('payment.messages.alreadyPaid') : '');
  const [formError, setFormError] = useState('');
  const [shippingDetails, setShippingDetails] = useState(initialShipping);
  const [addressPostalCode, setAddressPostalCode] = useState(() => normalizeJapanPostalCode(initialShipping?.postalCode || ''));
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const [intentState, setIntentState] = useState({
    loading: false,
    error: '',
    clientSecret: '',
    paymentIntentId: '',
    paymentStatus: paymentStatus,
    shippingCost: null,
    quikbakFee: null,
    totalAmount: typeof matchData?.paymentTotal === 'number' ? matchData.paymentTotal : null,
    currency: initialCurrency,
    succeeded: hasSucceeded,
  });

  const estimatePostalCode = addressPostalCode.length === 7 ? addressPostalCode : '';

  useEffect(() => {
    const nextPostalCode = normalizeJapanPostalCode(shippingDetails?.postalCode || initialShipping?.postalCode || '');
    if (!nextPostalCode) {
      return;
    }
    setAddressPostalCode((currentPostalCode) => (
      currentPostalCode === nextPostalCode ? currentPostalCode : nextPostalCode
    ));
  }, [initialShipping?.postalCode, shippingDetails?.postalCode]);

  // Fetch shipping estimate for Japan on mount
  useEffect(() => {
    if (hasSucceeded || !matchId || !matchData?.packageSize) {
      return;
    }
    if (estimate?.postalCode === estimatePostalCode) {
      return;
    }

    let cancelled = false;

    (async () => {
      setEstimateLoading(true);
      try {
        const response = await estimateShippingCost({
          matchId,
          country: JAPAN_COUNTRY_NAME,
          countryCode: JAPAN_COUNTRY_CODE,
          postalCode: estimatePostalCode,
        });
        const data = response?.data || response;
        if (cancelled) {
          return;
        }
        const newEstimate = {
          shippingCost: typeof data.shippingCost === 'number' ? data.shippingCost : null,
          quikbakFee: typeof data.quikbakFee === 'number' ? data.quikbakFee : DEFAULT_QUIKBAK_FEE,
          totalAmount: typeof data.totalAmount === 'number' ? data.totalAmount : null,
          currency: (data.currency || initialCurrency || 'jpy').toLowerCase(),
          countryName: JAPAN_COUNTRY_NAME,
          countryCode: JAPAN_COUNTRY_CODE,
          postalCode: estimatePostalCode,
        };
        setEstimate(newEstimate);

        onMatchUpdate({
          selectedShippingCountryName: JAPAN_COUNTRY_NAME,
          selectedShippingCountryCode: JAPAN_COUNTRY_CODE,
        });
      } catch (error) {
        console.error('Failed to get shipping estimate:', error);
        if (!cancelled) {
          setEstimate({
            shippingCost: null,
            quikbakFee: DEFAULT_QUIKBAK_FEE,
            totalAmount: null,
            currency: initialCurrency || 'jpy',
            countryName: JAPAN_COUNTRY_NAME,
            countryCode: JAPAN_COUNTRY_CODE,
            postalCode: estimatePostalCode,
          });
        }
      } finally {
        if (!cancelled) {
          setEstimateLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [estimate, estimatePostalCode, hasSucceeded, initialCurrency, matchData?.packageSize, matchId, onMatchUpdate]);

  const shippingAmountForTotal = intentState.shippingCost ?? estimate?.shippingCost ?? 0;
  const quikbakAmountForTotal = intentState.quikbakFee ?? estimate?.quikbakFee ?? DEFAULT_QUIKBAK_FEE;
  const totalDisplayAmount = intentState.totalAmount
    ?? estimate?.totalAmount
    ?? (shippingAmountForTotal + quikbakAmountForTotal);
  const shippingLocked = intentState.loading || paymentProcessing;

  const shippingContent = (() => {
    // Address step - Japan address form with postal code lookup
    if (step === 'address' && !intentState.succeeded) {
      return (
        <>
          {estimate && (
            <ShippingBreakdown
              t={t}
              shippingDetails={{ countryName: JAPAN_COUNTRY_NAME, countryCode: JAPAN_COUNTRY_CODE }}
              shippingCost={estimate.shippingCost}
              quikbakFee={estimate.quikbakFee}
              total={estimate.totalAmount}
              currency={estimate.currency}
              packageWeight={typeof matchData?.packageWeight === 'number' ? matchData.packageWeight : null}
              showAddress={false}
              title={t('payment.breakdown.title')}
            />
          )}
          <JapanAddressStep
            initialAddress={shippingDetails || initialShipping}
            onSubmit={(address, errorOverride) => handleAddressSubmit(address, errorOverride)}
            onPostalCodeChange={setAddressPostalCode}
            formError={formError}
            disabled={shippingLocked}
            hotelId={matchData?.hotelId}
          />
        </>
      );
    }

    if (step === 'payment' && !intentState.succeeded && intentState.clientSecret) {
      return (
        <>
          <ShippingBreakdown
            t={t}
            shippingDetails={shippingDetails}
            shippingCost={intentState.shippingCost ?? estimate?.shippingCost ?? null}
            quikbakFee={intentState.quikbakFee ?? estimate?.quikbakFee ?? DEFAULT_QUIKBAK_FEE}
            total={intentState.totalAmount ?? estimate?.totalAmount ?? null}
            currency={intentState.currency}
            packageWeight={typeof matchData?.packageWeight === 'number' ? matchData.packageWeight : null}
          />
          <div className="payment-action-row">
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                if (!shippingLocked) {
                  handleEditAddress();
                }
              }}
              disabled={shippingLocked}
            >
              {t('payment.buttons.editAddress')}
            </button>
          </div>
        </>
      );
    }

    return null;
  })();

  const handleEditAddress = useCallback(() => {
    if (intentState.loading || paymentProcessing) {
      return;
    }
    setStep('address');
    setStatusMessage('');
    setFormError('');
    setIntentState((prev) => ({
      ...prev,
      loading: false,
      error: '',
      clientSecret: '',
      paymentIntentId: '',
      paymentStatus: '',
      shippingCost: null,
      totalAmount: null,
      succeeded: false,
    }));
  }, [intentState.loading, paymentProcessing]);

  const requestPaymentIntent = useCallback(async (address, errorMessageOverride) => {
    if (!stripePromise) {
      return;
    }
    if (!address) {
      setFormError(errorMessageOverride || t('payment.errors.missingAddress'));
      return;
    }

    setFormError('');
    setStatusMessage('');
    setIntentState((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      const response = await createPaymentIntent({
        matchId,
        shippingAddress: buildShippingPayload(address),
      });

      const data = response?.data || response;
      const normalizedAddress = normalizeStoredAddress(data.shippingAddress) || address;

      setShippingDetails(normalizedAddress);
      setIntentState({
        loading: false,
        error: '',
        clientSecret: data.clientSecret || '',
        paymentIntentId: data.paymentIntentId || '',
        paymentStatus: data.paymentStatus || '',
        shippingCost: typeof data.shippingCost === 'number' ? data.shippingCost : null,
        quikbakFee: typeof data.quikbakFee === 'number' ? data.quikbakFee : DEFAULT_QUIKBAK_FEE,
        totalAmount: typeof data.totalAmount === 'number' ? data.totalAmount : data.amount ?? null,
        currency: (data.currency || initialCurrency || 'jpy').toLowerCase(),
        succeeded: Boolean(data.alreadySucceeded),
      });

      if (typeof data.shippingCost === 'number') {
        setEstimate((prev) => ({
          shippingCost: data.shippingCost,
          quikbakFee: typeof data.quikbakFee === 'number' ? data.quikbakFee : DEFAULT_QUIKBAK_FEE,
          totalAmount: typeof data.totalAmount === 'number' ? data.totalAmount : data.amount ?? null,
          currency: (data.currency || initialCurrency || 'jpy').toLowerCase(),
          countryName: normalizedAddress.countryName,
          countryCode: normalizedAddress.countryCode,
          postalCode: normalizeJapanPostalCode(normalizedAddress.postalCode),
        }));
      }

      if (data.alreadySucceeded) {
        setStep('completed');
        setStatusMessage(t('payment.messages.alreadyPaid'));
        onPaymentComplete({ ...data, shippingAddress: normalizedAddress });
      } else {
        setStep('payment');
      }
    } catch (error) {
      const message = resolveCallableErrorMessage(error, t);
      setIntentState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [initialCurrency, matchId, onPaymentComplete, t]);

  const handleProhibitedItemsAcknowledged = useCallback(() => {
    setShowProhibitedItemsDialog(false);
    setHasAcknowledgedProhibited(true);
    if (pendingAddress) {
      requestPaymentIntent(pendingAddress, pendingErrorOverride || undefined);
      setPendingAddress(null);
      setPendingErrorOverride('');
    }
  }, [pendingAddress, pendingErrorOverride, requestPaymentIntent]);

  const handleAddressSubmit = useCallback((address, errorMessageOverride) => {
    if (!hasAcknowledgedProhibited) {
      setPendingAddress(address);
      setPendingErrorOverride(errorMessageOverride || '');
      setShowProhibitedItemsDialog(true);
      return;
    }
    requestPaymentIntent(address, errorMessageOverride);
  }, [hasAcknowledgedProhibited, requestPaymentIntent]);

  useEffect(() => {
    if (hasSucceeded) {
      setStep('completed');
      setStatusMessage(t('payment.messages.alreadyPaid'));
    }
  }, [hasSucceeded, t]);

  const handlePaymentSuccess = (message) => {
    setStatusMessage(message);
    setIntentState((prev) => ({ ...prev, succeeded: true }));
    setStep('completed');
  };

  const handleErrorMessage = (message) => {
    setIntentState((prev) => ({ ...prev, error: message || '' }));
  };

  if (!stripePromise) {
    return (
      <div className="payment-section">
        <h3>{t('payment.title')}</h3>
        <div className="info-message">{t('payment.messages.missingKey')}</div>
      </div>
    );
  }

  return (
    <div className="payment-section">
      <h3>{t('payment.title')}</h3>
      <p className="payment-description">{t('payment.description')}</p>

      {intentState.loading && <div className="loading-container">{t('payment.messages.loading')}</div>}

      {!intentState.loading && intentState.error && (
        <div className="error-message" role="alert">
          {intentState.error}
          {!intentState.clientSecret && (
            <div>
              <button
                type="button"
                className="secondary-button"
                onClick={() => handleAddressSubmit(shippingDetails || initialShipping)}
              >
                {t('payment.actions.retry')}
              </button>
            </div>
          )}
        </div>
      )}

      {statusMessage && (
        <div className="success-message" role="status">
          {statusMessage}
        </div>
      )}

      {shippingContent && (
        <div
          className={`shipping-step-wrapper${shippingLocked ? ' shipping-step-wrapper--locked' : ''}`}
          aria-busy={shippingLocked}
        >
          {shippingContent}
          {shippingLocked && <div className="shipping-step-overlay" aria-hidden="true" />}
        </div>
      )}

      {step === 'payment' && !intentState.succeeded && intentState.clientSecret && (
        <Elements
          key={intentState.clientSecret}
          stripe={stripePromise}
          options={{ clientSecret: intentState.clientSecret }}
        >
          <CheckoutForm
            matchId={matchId}
            paymentIntentId={intentState.paymentIntentId}
            amount={totalDisplayAmount || 0}
            currency={intentState.currency}
            shippingDetails={shippingDetails}
            onPaymentComplete={(payload) => {
              const normalizedAddress = normalizeStoredAddress(payload.shippingAddress) || shippingDetails;
              setShippingDetails(normalizedAddress);
              setIntentState((prev) => ({
                ...prev,
                paymentStatus: payload.paymentStatus || 'succeeded',
                shippingCost: typeof payload.shippingCost === 'number' ? payload.shippingCost : prev.shippingCost,
                quikbakFee: typeof payload.quikbakFee === 'number' ? payload.quikbakFee : prev.quikbakFee,
                totalAmount: typeof payload.totalAmount === 'number' ? payload.totalAmount : prev.totalAmount,
                succeeded: true,
              }));
              handlePaymentSuccess(t('payment.messages.success'));
              onPaymentComplete({
                ...payload,
                shippingAddress: normalizedAddress,
              });
            }}
            onError={handleErrorMessage}
            onSuccess={handlePaymentSuccess}
            onProcessingChange={setPaymentProcessing}
          />
        </Elements>
      )}

      <ProhibitedItemsAcknowledgment
        isOpen={showProhibitedItemsDialog}
        onClose={() => setShowProhibitedItemsDialog(false)}
        onConfirm={handleProhibitedItemsAcknowledged}
      />
    </div>
  );
}
